import api from './api';

const CACHE_KEY = 'smartdocs_feature_flags';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export interface FeatureFlags {
  contractor_app_sso?: boolean;
  [key: string]: boolean | undefined;
}

interface CachedFlags {
  flags: FeatureFlags;
  timestamp: number;
}

const DEFAULT_FLAGS: FeatureFlags = {
  contractor_app_sso: false,
};

function getCached(): FeatureFlags | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed: CachedFlags = JSON.parse(raw);
    if (!parsed.flags || typeof parsed.timestamp !== 'number') return null;
    if (Date.now() - parsed.timestamp > CACHE_TTL_MS) return null;
    return parsed.flags;
  } catch {
    return null;
  }
}

function setCache(flags: FeatureFlags): void {
  try {
    const data: CachedFlags = { flags, timestamp: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // Ignore storage errors
  }
}

export const configApi = {
  /**
   * Fetch feature flags from API. On success, updates localStorage cache.
   * On error, returns cached value if fresh, else safe defaults.
   */
  async getFeatureFlags(): Promise<FeatureFlags> {
    try {
      const response = await api.get<{ success: boolean; data: FeatureFlags }>('/config/flags');
      const flags = response.data?.data ?? DEFAULT_FLAGS;
      setCache(flags);
      return flags;
    } catch {
      const cached = getCached();
      return cached ?? DEFAULT_FLAGS;
    }
  },

  /** Read flags from cache only (no network). Returns null if stale/missing. */
  getCachedFlags(): FeatureFlags | null {
    return getCached();
  },

  /** Get flags synchronously for initial render: cache or defaults. */
  getFlagsSync(): FeatureFlags {
    return getCached() ?? DEFAULT_FLAGS;
  },
};
